import { useState } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { Spinner } from '@/components/Spinner';
import { Page } from '@/components/layout/Page';
import { cls } from '@/utils/cls';

// ── Color tokens (mock CSS vars → hex) ──────────────────────────────────────
const C = {
  blueDark: '#274483',
  text1: '#1A1C2E',
  text2: '#5C5F72',
  text3: '#9295A5',
  border: '#E4E0D8',
  bgWarm: '#F5F6F8',
  white: '#FFFFFF',
};

// ── Types ───────────────────────────────────────────────────────────────────
// Real Order-to-Cash event row (from /api/data-app/events)
type EventRow = {
  eventId: string;
  orderId: string;
  lineItemId: string;
  orderItemId: string;
  productId: string;
  product: string;
  custId: string;
  customer: string;
  eventType: string;
  eventTime: string;
  color: string;
};
type EventsResp = { items: EventRow[]; page: number; pageSize: number; total: number | null; hasMore: boolean };
// Real order detail (from /api/data-app/order/:orderId)
type OrderLineItem = {
  id: string;
  product: string;
  productId: string;
  qty: string;
  rdd: string;
  commit: string;
  risk: string;
  riskLabel: string;
  intel: unknown[];
  events: { type: string; date: string; cat: string; note: string; risk: boolean; color: string }[];
};
type OrderDetailResp = {
  product: string;
  qty: string;
  orderDate: string;
  commitDate: string;
  lineItems: OrderLineItem[];
};
type CustomerDescribe = { id: string; name: string; city: string; country: string; segment: string; type: string };
type ProductDescribe = {
  id: string;
  name: string;
  businessGroup: string;
  productLine: string;
  productClass: string;
  baseUom: string;
  saleUom: string;
  status: string;
};

const UPPER_LABEL = 'font-bold uppercase';
const O2C_PAGE_SIZE = 10;
const STALE = 5 * 60 * 1000;

export function DataEvents() {
  return (
    <Page
      title="Event Streams"
      subheader="Canonical business events reconstructed from source system state changes, ordered at line-item grain — with Signal vs Decision classification, Δt measurements, and full source evidence."
      fillHeight
    >
      <div className="flex flex-col h-full min-h-0">
        {/* Order to Cash is the only real event family (the others were fake mock data, removed). */}
        <div className="flex items-center gap-1.5 pb-1 flex-shrink-0">
          <span
            className="text-[11.5px] font-semibold px-[14px] py-[5px] whitespace-nowrap"
            style={{ borderRadius: 4, background: C.blueDark, color: 'white' }}
          >
            Order to Cash
          </span>
        </div>

        {/* Master-detail split */}
        <div
          className="flex flex-1 min-h-0 overflow-hidden bg-white mt-2"
          style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}
        >
          <O2CEventsView />
        </div>
      </div>
    </Page>
  );
}

// ── REAL Order-to-Cash view (server-paginated over ALL events) ────────────────
function O2CEventsView() {
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<EventRow | null>(null);
  // Permalink: ?id=<orderId>. A shared link selects that order (degrades to id-only detail if it's not on the
  // current page — the list is server-paginated, so customer/product identity may be missing until clicked).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlOrder = searchParams.get('id');
  const selectRow = (r: EventRow) => {
    setSelectedRow(r);
    setSearchParams(
      (prev) => {
        prev.set('id', r.orderId);
        return prev;
      },
      { replace: true },
    );
  };

  const { data, isFetching, isPlaceholderData } = useQuery<EventsResp>({
    queryKey: ['data-app', 'events', page],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/events?page=${page}&pageSize=${O2C_PAGE_SIZE}`);
      if (!r.ok) throw new Error('Failed to load events');
      return r.json();
    },
    placeholderData: keepPreviousData,
    staleTime: STALE,
  });

  const pageItems = data?.items ?? [];
  const total = data?.total ?? null;
  const hasMore = data?.hasMore ?? false;
  const pageCount = total != null ? Math.max(1, Math.ceil(total / O2C_PAGE_SIZE)) : null;
  const urlRow: EventRow | null = urlOrder
    ? (pageItems.find((r) => r.orderId === urlOrder) ?? {
        eventId: '',
        orderId: urlOrder,
        lineItemId: '',
        orderItemId: '',
        productId: '',
        product: '',
        custId: '',
        customer: '',
        eventType: '',
        eventTime: '',
        color: '',
      })
    : null;
  const effectiveRow = selectedRow ?? urlRow ?? pageItems[0] ?? null;

  return (
    <>
      {/* LEFT: real event list */}
      <div
        className="w-[380px] flex-shrink-0 flex flex-col overflow-hidden"
        style={{ borderRight: `1px solid ${C.border}` }}
      >
        <div className="px-3 py-[7px] flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-[6px]">
            <span className="text-[11px] font-bold" style={{ color: C.text1 }}>
              Order Events
            </span>
            <span className="text-[10px] font-semibold" style={{ color: C.text2 }}>
              {total != null ? `${total.toLocaleString('en-US')} events` : ''}
            </span>
          </div>
          {/* Pagination (10/page over the full feed) — styling matches Customers/Products */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: page <= 1 ? C.text3 : C.blueDark,
                cursor: page <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              ‹ Prev
            </button>
            <span style={{ fontSize: 10, color: C.text2 }}>
              {page}
              {pageCount != null ? ` / ${pageCount.toLocaleString('en-US')}` : ''}{' '}
              <span style={{ color: C.text3 }}>· 10/page</span>
            </span>
            <button
              type="button"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 6,
                border: `1px solid ${C.border}`,
                background: '#fff',
                color: !hasMore ? C.text3 : C.blueDark,
                cursor: !hasMore ? 'not-allowed' : 'pointer',
              }}
            >
              Next ›
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isPlaceholderData || (isFetching && pageItems.length === 0) ? (
            // Loading — first page load or paging to a new page. Show a spinner instead of stale/blank rows.
            <div className="flex items-center justify-center" style={{ padding: '48px 0' }}>
              <Spinner size={26} />
            </div>
          ) : pageItems.length === 0 ? (
            <div className="px-4 py-8 text-center text-[11.5px]" style={{ color: C.text3 }}>
              No order events available.
            </div>
          ) : (
            pageItems.map((r) => {
              const sel = effectiveRow?.eventId === r.eventId;
              return (
                <div
                  key={r.eventId || `${r.orderId}-${r.eventType}`}
                  onClick={() => selectRow(r)}
                  className="px-3 py-[9px] cursor-pointer transition-colors hover:bg-[rgba(39,68,131,.04)]"
                  style={{
                    borderBottom: `1px solid ${C.border}`,
                    ...(sel ? { background: 'rgba(39,68,131,.07)', borderLeft: `3px solid ${C.blueDark}` } : {}),
                  }}
                >
                  {/* Order selector: order id + ship-to customer only (an order spans many line
                      items / products / events, so per-event product/type/date would be misleading). */}
                  <div className="text-[11.5px] font-bold truncate mb-[2px]" style={{ color: C.text1 }}>
                    {r.orderId}
                  </div>
                  <div className="text-[10.5px] truncate" style={{ color: C.text2 }}>
                    {r.customer || '—'}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT: real detail */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {!effectiveRow ? (
          isFetching ? (
            // First load — show a spinner instead of the empty placeholder.
            <div className="flex-1 flex items-center justify-center">
              <Spinner size={28} />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-2" style={{ color: C.text3 }}>
              <div className="text-[32px]" style={{ opacity: 0.2 }}>
                ⚡
              </div>
              <div className="text-[13px] font-semibold" style={{ color: C.text2 }}>
                Select an order event
              </div>
              <div className="text-[11.5px] text-center max-w-[300px] leading-[1.6]">
                Click any order to see its line items, full event sequence, source evidence, and the linked customer and
                product.
              </div>
            </div>
          )
        ) : (
          <O2CDetail key={effectiveRow.orderId} row={effectiveRow} />
        )}
      </div>
    </>
  );
}

function dtDays(a: string, b: string): string {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return 'Δt';
  const d = Math.round(Math.abs(tb - ta) / 86_400_000);
  return d === 0 ? '<1d' : `${d}d`;
}

function O2CDetail({ row }: { row: EventRow }) {
  const [tab, setTab] = useState<'events' | 'evidence'>('events');
  const [liId, setLiId] = useState<string | null>(null);

  const { data: order, isLoading } = useQuery<OrderDetailResp>({
    queryKey: ['data-app', 'order', row.orderId],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/order/${encodeURIComponent(row.orderId)}`);
      if (!r.ok) throw new Error('Failed to load order');
      return r.json();
    },
    enabled: !!row.orderId,
    staleTime: STALE,
  });
  const { data: cust } = useQuery<CustomerDescribe>({
    queryKey: ['data-app', 'customers', row.custId],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/customers/${encodeURIComponent(row.custId)}`);
      if (!r.ok) throw new Error('Failed to load customer');
      return r.json();
    },
    enabled: !!row.custId,
    staleTime: STALE,
  });
  const lineItems = order?.lineItems ?? [];
  const effLiId = liId ?? lineItems[0]?.id ?? null;
  const li = lineItems.find((l) => l.id === effLiId) ?? null;
  const events = li?.events ?? [];
  // Product reflects the SELECTED line item — each line item in an order can be a different SKU,
  // while the ship-to customer is shared across the order. Falls back to the event row pre-load.
  const activeProductId = li?.productId || row.productId;
  const activeProductName = li?.product || row.product;

  const { data: prod } = useQuery<ProductDescribe>({
    queryKey: ['data-app', 'products', activeProductId],
    queryFn: async () => {
      const r = await fetch(`/api/data-app/products/${encodeURIComponent(activeProductId)}`);
      if (!r.ok) throw new Error('Failed to load product');
      return r.json();
    },
    enabled: !!activeProductId,
    staleTime: STALE,
  });

  const custLink = row.custId ? `/data/customers?id=${encodeURIComponent(row.custId)}` : null;
  const prodLink = activeProductId ? `/data/products?id=${encodeURIComponent(activeProductId)}` : null;
  const custName = cust?.name || row.customer || row.custId;
  const prodName = prod?.name || activeProductName || activeProductId;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Detail header */}
      <div className="px-5 pt-[14px] pb-3 flex-shrink-0 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="text-[15px] font-bold" style={{ color: C.text1, letterSpacing: '-0.02em' }}>
          {li?.id || row.lineItemId || row.orderId}
        </div>
        {/* Order id is already in the title above; customer + product show larger just below. */}
        <div className="text-[10.5px] mt-[2px]" style={{ color: C.text3 }}>
          Order · SAP ERP
        </div>
      </div>

      {/* Associated customer + product (describe APIs) */}
      <div
        className="grid grid-cols-2 flex-shrink-0 bg-white text-[10.5px]"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div className="px-5 py-3" style={{ borderRight: `1px solid ${C.border}` }}>
          <div className={cls(UPPER_LABEL, 'text-[8.5px] tracking-[0.08em] mb-[5px]')} style={{ color: C.text3 }}>
            Customer
          </div>
          {custLink ? (
            <Link to={custLink} className="text-[12px] font-bold hover:underline" style={{ color: C.blueDark }}>
              {custName}
            </Link>
          ) : (
            <span className="text-[12px] font-bold" style={{ color: C.text1 }}>
              {custName}
            </span>
          )}
          <div className="mt-[3px] leading-[1.5]" style={{ color: C.text2 }}>
            {[cust?.city, cust?.country].filter(Boolean).join(', ') || '—'}
            {cust?.type ? ` · ${cust.type}` : ''}
          </div>
          <div className="text-[9.5px] mt-px font-mono" style={{ color: C.text3 }}>
            {row.custId}
          </div>
        </div>
        <div className="px-5 py-3">
          <div className={cls(UPPER_LABEL, 'text-[8.5px] tracking-[0.08em] mb-[5px]')} style={{ color: C.text3 }}>
            Product
          </div>
          {prodLink ? (
            <Link to={prodLink} className="text-[12px] font-bold hover:underline" style={{ color: C.blueDark }}>
              {prodName}
            </Link>
          ) : (
            <span className="text-[12px] font-bold" style={{ color: C.text1 }}>
              {prodName}
            </span>
          )}
          <div className="mt-[3px] leading-[1.5]" style={{ color: C.text2 }}>
            {prod?.businessGroup || prod?.productLine || '—'}
            {prod?.status ? ` · ${prod.status}` : ''}
          </div>
          <div className="text-[9.5px] mt-px font-mono" style={{ color: C.text3 }}>
            {row.productId}
          </div>
        </div>
      </div>

      {/* Line-item selector (shown for any order with ≥1 line item, for consistency) */}
      {lineItems.length > 0 && (
        <div className="px-5 py-2 flex-shrink-0 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className={cls(UPPER_LABEL, 'text-[9px] tracking-[0.08em] mb-[6px]')} style={{ color: C.text3 }}>
            Available Line Items · {lineItems.length}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {lineItems.map((l) => {
              const on = l.id === effLiId;
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLiId(l.id)}
                  className="text-[9.5px] font-semibold px-2 py-[3px] rounded-full whitespace-nowrap"
                  style={
                    on
                      ? { background: C.blueDark, color: 'white', border: '1px solid transparent' }
                      : { background: C.bgWarm, color: C.text2, border: `1px solid ${C.border}` }
                  }
                >
                  {l.id}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Lifecycle timeline (real events; Δt computed from real event dates) */}
      <div className="px-5 py-[14px] flex-shrink-0 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className={cls(UPPER_LABEL, 'text-[9px] tracking-[0.08em] mb-[10px]')} style={{ color: C.text3 }}>
          Line Item Lifecycle
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="text-[11px] py-2" style={{ color: C.text3 }}>
              Loading events…
            </div>
          ) : events.length === 0 ? (
            <div className="text-[11px] py-2" style={{ color: C.text3 }}>
              No reconstructed events for this line item.
            </div>
          ) : (
            <div className="flex items-start py-1 w-max">
              {/* Content-sized (w-max) so few events don't sprawl across the panel; connectors are a
                  fixed width (capped gap) so the row grows + scrolls for many events without overlapping.
                  Top-aligned + the connector pushed down to the circle's mid-line so the Δt label sits beside
                  the circles, clear of the (often two-line) event-type names below them. */}
              {events.map((ev, i) => (
                <div key={i} className="contents">
                  {/* Node hugs its (single-line) label width + padding, so longer event-type names just push the
                      circles farther apart — the label never wraps and never bleeds into the Δt connector. */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0 px-3">
                    <div
                      className="w-7 h-7 rounded-full flex-shrink-0"
                      style={{ background: ev.color || C.blueDark, border: '2px solid transparent' }}
                    />
                    <div
                      className="text-[9px] font-semibold text-center leading-[1.3] whitespace-nowrap"
                      style={{ color: C.text2 }}
                    >
                      {ev.type}
                    </div>
                    <div className="text-[8.5px]" style={{ color: C.text3 }}>
                      {ev.date}
                    </div>
                  </div>
                  {i < events.length - 1 && (
                    <div
                      className="flex flex-col items-center gap-[2px] flex-shrink-0"
                      style={{ width: 72, marginTop: 9 }}
                    >
                      <div className="h-[2px] w-full" style={{ background: C.border }} />
                      <div className="text-[8.5px] font-bold whitespace-nowrap" style={{ color: C.text3 }}>
                        {dtDays(ev.date, events[i + 1].date)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-shrink-0 px-4 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        {(
          [
            { key: 'events', label: 'Events' },
            { key: 'evidence', label: 'Source Evidence' },
          ] as const
        ).map((t) => {
          const active = tab === t.key;
          return (
            <div
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-3 py-[5px] text-[11px] cursor-pointer whitespace-nowrap transition-all"
              style={{
                fontWeight: active ? 600 : 500,
                color: active ? C.blueDark : C.text3,
                borderBottom: `2px solid ${active ? C.blueDark : 'transparent'}`,
              }}
            >
              {t.label}
            </div>
          );
        })}
      </div>

      {/* Tab panes */}
      <div className="flex-1 overflow-y-auto min-h-0 bg-white">
        {tab === 'events' ? (
          <div className="px-5 py-[14px]">
            <div className={cls(UPPER_LABEL, 'text-[9px] tracking-[0.1em] mb-[10px]')} style={{ color: C.text3 }}>
              {events.length} Event{events.length === 1 ? '' : 's'} Reconstructed
            </div>
            {events.map((ev, i) => (
              <div
                key={i}
                className="flex items-start gap-[10px] py-2"
                style={{ borderBottom: i < events.length - 1 ? `1px solid ${C.border}` : 'none' }}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
                  style={{ background: ev.color || C.blueDark }}
                />
                <div className="flex-1">
                  <div className="text-[12px] font-semibold" style={{ color: C.text1 }}>
                    {ev.type}
                  </div>
                  <div className="text-[10px] mt-[2px]" style={{ color: C.text3 }}>
                    {ev.date}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 py-[14px]">
            <div className={cls(UPPER_LABEL, 'text-[9px] tracking-[0.1em] mb-[10px]')} style={{ color: C.text3 }}>
              Source Evidence — Reconstructed line-item events
            </div>
            {events.map((ev, i) => (
              <div
                key={i}
                className="px-3 py-[10px] mb-2"
                style={{ borderRadius: 6, background: C.white, border: `1px solid ${C.border}` }}
              >
                <div className="text-[12px] font-bold mb-[6px]" style={{ color: C.text1 }}>
                  {ev.type}{' '}
                  <span className="text-[10px] font-normal" style={{ color: C.text3 }}>
                    · {ev.date}
                  </span>
                </div>
                {[
                  { key: 'Event', val: ev.type },
                  { key: 'Event Date', val: ev.date },
                  { key: 'Line Item', val: li?.id ?? row.lineItemId },
                  { key: 'Source', val: 'SAP ERP · item-lifecycle' },
                ].map((f, j, arr) => (
                  <div
                    key={f.key}
                    className="flex justify-between items-baseline gap-3 py-[3px]"
                    style={{ borderBottom: j < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}
                  >
                    <span className="text-[10px] font-mono flex-shrink-0" style={{ color: C.text3 }}>
                      {f.key}
                    </span>
                    <span className="text-[10.5px] font-semibold text-right" style={{ color: C.text1 }}>
                      {f.val}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
